/**
 * Concepts documentation page
 */

import {DocsContent} from '../../components/docs/DocsContent';
import {DocsTable} from '../../components/docs/DocsTable';
import {DocsActionButtons} from '../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';
import {ROUTES} from '../../config/constants';

export function Concepts() {
  return (
    <DocsContent
      title="Core Concepts"
      description="Understand how VoxaPilot works, including transcription, AI summaries, and speaker detection."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">How VoxaPilot Works</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot uses advanced AI to transform your meetings into actionable insights. Here's
            how it works:
          </p>

          <ol className="list-decimal list-inside space-y-4 text-textSecondary mb-6">
            <li>
              <strong className="text-textPrimary">Recording:</strong> VoxaPilot connects to your
              meeting platform or accepts audio uploads
            </li>
            <li>
              <strong className="text-textPrimary">Transcription:</strong> Real-time or post-meeting
              transcription using advanced speech recognition
            </li>
            <li>
              <strong className="text-textPrimary">Processing:</strong> AI analyzes the transcript
              to identify speakers, extract key points, and generate summaries
            </li>
            <li>
              <strong className="text-textPrimary">Insights:</strong> Action items, decisions, and
              insights are automatically extracted and organized
            </li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Transcription</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot uses state-of-the-art speech recognition models to transcribe your meetings
            with high accuracy. The transcription process includes:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Real-time transcription for live meetings</li>
            <li>Post-meeting transcription for uploaded audio files</li>
            <li>Speaker identification and labeling</li>
            <li>Timestamp generation for easy navigation</li>
            <li>Support for multiple languages</li>
          </ul>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Accuracy</h3>
          <p className="text-textSecondary mb-4">
            VoxaPilot achieves 99% accuracy for clear audio. Accuracy may vary with:
          </p>
          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Background noise</li>
            <li>Accents and dialects</li>
            <li>Audio quality</li>
            <li>Multiple speakers talking simultaneously</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">AI Summaries</h2>
          <p className="text-textSecondary mb-4">
            After transcription, VoxaPilot uses AI to generate intelligent summaries that include:
          </p>

          <DocsTable
            headers={['Feature', 'Description']}
            rows={[
              ['Meeting Summary', 'High-level overview of the meeting'],
              ['Action Items', 'Tasks and follow-ups extracted from the conversation'],
              ['Key Decisions', 'Important decisions made during the meeting'],
              ['Topics Discussed', 'Main topics and themes identified'],
              ['Sentiment Analysis', 'Overall tone and sentiment of the meeting'],
            ]}
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Speaker Detection</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot automatically identifies and labels speakers in your meetings. The system:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Learns speaker voices during the meeting</li>
            <li>Assigns labels (Speaker 1, Speaker 2, or custom names)</li>
            <li>Maintains consistency across the transcript</li>
            <li>Allows manual correction and labeling</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Data Storage & Privacy</h2>
          <p className="text-textSecondary mb-4">
            Your meeting data is encrypted in transit and at rest. VoxaPilot:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Uses end-to-end encryption for all data</li>
            <li>Complies with SOC 2, GDPR, and HIPAA standards</li>
            <li>Never shares your data with third parties</li>
            <li>Allows you to delete data at any time</li>
            <li>Provides configurable retention policies</li>
          </ul>

          <p className="text-textSecondary dark:text-[#858585]">
            Learn more about{' '}
            <Link to={ROUTES.SECURITY} className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
              security and privacy
            </Link>
            .
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/features/transcription" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about transcription features
              </Link>
            </li>
            <li>
              <Link to="/docs/features/summaries" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Explore AI summaries
              </Link>
            </li>
            <li>
              <Link to="/docs/models" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                See available AI models
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="Core Concepts" />
      </div>
    </DocsContent>
  );
}

